import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Package,
  CheckCircle,
  FileText,
  Upload,
  Eye,
  CalendarBlank,
  CurrencyDollar,
  Clock,
  ArrowLeft,
  Truck,
  Spinner,
  DownloadSimple
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CURRENCY_SYMBOLS = {
  USD: '$',
  XCD: 'EC$',
  JMD: 'J$',
  TTD: 'TT$'
};

const STATUS_COLORS = {
  po_issued: 'bg-[#0F52BA]/10 text-[#0F52BA]',
  vendor_confirmed: 'bg-[#2D6A4F]/10 text-[#2D6A4F]',
  documents_uploaded: 'bg-[#F4A261]/10 text-[#B45309]'
};

// Vendor Portal only shows these three status labels
const STATUS_LABELS = {
  po_issued: 'Awaiting Confirmation',
  vendor_confirmed: 'Confirmed',
  documents_uploaded: 'Documents Uploaded'
};

// Map backend statuses to vendor-visible statuses
const getVendorStatus = (backendStatus) => {
  // Map any shipment/transit/delivery statuses to 'documents_uploaded' for vendor view
  const vendorStatusMap = {
    'po_issued': 'po_issued',
    'vendor_confirmed': 'vendor_confirmed',
    'documents_uploaded': 'documents_uploaded',
    'in_transit': 'documents_uploaded',
    'delivered': 'documents_uploaded',
    'closed': 'documents_uploaded'
  };
  return vendorStatusMap[backendStatus] || 'documents_uploaded';
};

const DOCUMENT_TYPES = [
  { value: 'original_vendor_invoice', label: 'Invoice', required: false, order: 1 },
  { value: 'packing_list', label: 'Packing List', order: 2 },
  { value: 'hazmat_documents', label: 'Hazmat Documents', conditional: true, order: 3 },
  { value: 'material_data_safety_sheet', label: 'Material Data Safety Sheet', conditional: true, order: 4 },
  { value: 'other', label: 'Other Supporting Documents', order: 5 }
];

const VendorPortalPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPO, setSelectedPO] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadData, setUploadData] = useState({ document_type: '', filename: '', document_url: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPOs();
  }, []);

  const fetchPOs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/vendor-portal/my-pos`, getAuthHeaders());
      setPOs(res.data);
    } catch (error) {
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const viewPODetail = async (poId) => {
    try {
      const res = await axios.get(`${API_URL}/api/vendor-portal/po/${poId}`, getAuthHeaders());
      setSelectedPO(res.data);
    } catch (error) {
      toast.error('Failed to load PO details');
    }
  };

  const confirmReceipt = async () => {
    if (!selectedPO) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/vendor-portal/po/${selectedPO.id}/confirm`, {}, getAuthHeaders());
      toast.success('PO confirmed successfully');
      await viewPODetail(selectedPO.id);
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm PO');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPOPdf = async (poId) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API_URL}/api/purchase-orders/${poId}/pdf`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'purchase_order.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  const uploadDocument = async () => {
    if (!selectedPO || !uploadData.document_type || !uploadData.filename) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      // For demo, create a mock URL
      const docUrl = `/documents/${selectedPO.po_number}/${uploadData.filename}`;
      await axios.post(
        `${API_URL}/api/vendor-portal/po/${selectedPO.id}/upload-document`,
        { ...uploadData, document_url: docUrl },
        getAuthHeaders()
      );
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadData({ document_type: '', filename: '', document_url: '' });
      await viewPODetail(selectedPO.id);
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F52BA] border-t-transparent"></div>
      </div>
    );
  }

  // Detail View
  if (selectedPO) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedPO(null)} className="p-2">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {selectedPO.po_number}
            </h1>
            <p className="text-slate-500">Purchase Order Details</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={() => downloadPOPdf(selectedPO.id)}
            className="bg-[#3D1D72] hover:bg-[#2D1652]"
            data-testid="download-po-pdf-btn"
          >
            <DownloadSimple size={18} className="mr-2" />
            Download PO as PDF
          </Button>
          {selectedPO.vendor_confirmation_status !== 'accepted' && (
            <Button
              onClick={confirmReceipt}
              disabled={submitting}
              className="bg-[#2D6A4F] hover:bg-[#1B4D3E]"
              data-testid="confirm-po-btn"
            >
              {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
              Confirm Receipt of PO
            </Button>
          )}
          <Button
            onClick={() => setUploadDialogOpen(true)}
            variant="outline"
            data-testid="upload-doc-btn"
          >
            <Upload size={18} className="mr-2" />
            Upload Documents
          </Button>
        </div>

        {/* PO Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Status</p>
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium mt-1 ${STATUS_COLORS[getVendorStatus(selectedPO.status)]}`}>
              {STATUS_LABELS[getVendorStatus(selectedPO.status)]}
            </span>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Total Value</p>
            <p className="text-xl font-bold text-slate-800 mt-1">
              {CURRENCY_SYMBOLS[selectedPO.currency]}{selectedPO.total_value.toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Required Delivery</p>
            <p className="text-lg font-medium text-slate-800 mt-1">
              {format(new Date(selectedPO.required_delivery_date), 'MMM d, yyyy')}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Payment Terms</p>
            <p className="text-lg font-medium text-slate-800 mt-1">{selectedPO.payment_terms}</p>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Line Items</h2>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Item</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Unit</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Unit Price</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {selectedPO.line_items?.map((item, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-3 font-medium text-slate-800">{item.item_name}</td>
                  <td className="px-4 py-3 text-slate-600">{item.quantity}</td>
                  <td className="px-4 py-3 text-slate-600">{item.unit}</td>
                  <td className="px-4 py-3 text-slate-600">{CURRENCY_SYMBOLS[selectedPO.currency]}{item.unit_price?.toFixed(2)}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{CURRENCY_SYMBOLS[selectedPO.currency]}{item.total_price?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Uploaded Documents */}
        <div className="bg-white rounded-xl border border-slate-100">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Uploaded Documents</h2>
            <p className="text-xs text-slate-500 mt-1">
              Upload any invoice/sales document showing item descriptions and values (commercial, proforma, sales order, supplier receipt etc) along with supporting documents.
            </p>
          </div>
          <div className="p-4">
            {selectedPO.documents?.length > 0 ? (
              <div className="space-y-3">
                {[...selectedPO.documents]
                  .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))
                  .map((doc, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                      data-testid={`vendor-doc-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={24} className="text-[#0F52BA]" />
                        <div>
                          <p className="font-medium text-slate-800">{doc.filename}</p>
                          <p className="text-xs text-slate-500">
                            {DOCUMENT_TYPES.find(t => t.value === doc.type)?.label || doc.type} •{' '}
                            {format(new Date(doc.uploaded_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">Download</Button>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-slate-500">No documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Upload Document Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Upload Documents</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 mt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-medium">Invoice Uploads</p>
                <p className="text-xs text-blue-700 mt-1">
                  Any invoice / sales document is acceptable — commercial invoice, proforma invoice, sales order, online retailer invoice (Amazon, supplier website), supplier receipt — as long as it shows item descriptions and values.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select value={uploadData.document_type} onValueChange={(v) => setUploadData({ ...uploadData, document_type: v })}>
                  <SelectTrigger data-testid="doc-type-select">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="original_vendor_invoice" className="font-medium">Invoice</SelectItem>
                    <SelectItem value="packing_list">Packing List</SelectItem>
                    <SelectItem value="hazmat_documents">
                      <div className="flex flex-col">
                        <span>Hazmat Documents</span>
                        <span className="text-xs text-slate-500">if applicable</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="material_data_safety_sheet">
                      <div className="flex flex-col">
                        <span>Material Data Safety Sheet</span>
                        <span className="text-xs text-slate-500">if applicable</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="other">Other Supporting Documents</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>File Name</Label>
                <Input
                  value={uploadData.filename}
                  onChange={(e) => setUploadData({ ...uploadData, filename: e.target.value })}
                  placeholder="e.g., invoice_123.pdf"
                  data-testid="doc-filename-input"
                />
              </div>
              
              <p className="text-xs text-slate-500">
                Note: In production, you would upload an actual file. For demo purposes, enter the filename.
              </p>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                <Button onClick={uploadDocument} disabled={submitting} className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="upload-submit-btn">
                  {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <Upload size={18} className="mr-2" />}
                  Upload
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/10 flex items-center justify-center">
          <Package size={24} className="text-[#7C3AED]" />
        </div>
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Vendor Portal
          </h1>
          <p className="text-slate-500">Welcome, {user?.full_name}</p>
        </div>
      </div>

      {/* Key Actions - 2 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-[#7C3AED] to-[#A78BFA] rounded-xl p-6 text-white">
          <CheckCircle size={32} className="mb-3" />
          <h3 className="text-xl font-bold">Confirm POs</h3>
          <p className="text-white/80 text-sm mt-1">{pos.filter(p => p.vendor_confirmation_status !== 'accepted').length} orders awaiting confirmation</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <Upload size={32} className="text-[#7C3AED] mb-3" />
          <h3 className="text-xl font-bold text-slate-800">Upload Documents</h3>
          <p className="text-slate-500 text-sm mt-1">Invoice, packing list, shipping docs</p>
        </div>
      </div>

      {/* My Purchase Orders */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">My Purchase Orders</h2>
          <p className="text-sm text-slate-500">{pos.length} orders</p>
        </div>

        {pos.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Package size={48} className="mx-auto mb-4 text-slate-300" />
            <p>No purchase orders assigned to you</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pos.map(po => (
                  <tr key={po.id} className="hover:bg-slate-50/50" data-testid={`vendor-po-row-${po.id}`}>
                    <td className="px-4 py-4 font-medium text-slate-800">{po.po_number}</td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[getVendorStatus(po.status)]}`}>
                        {STATUS_LABELS[getVendorStatus(po.status)]}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <Button variant="ghost" size="sm" onClick={() => viewPODetail(po.id)} data-testid={`view-po-${po.id}`}>
                        <Eye size={18} className="mr-1" />
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorPortalPage;
